import React, { FC, createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useUserContext } from '@blocs.auth';
import { captureExceptionEvent } from '@blocs.observability';
import { useDeferredPromise } from '@minecraft.utils';
import { PlanType, usePlans } from './usePlans';
import { useBilling as useBillingHook } from './useBilling';
import { usePayment } from './usePayment';
import { useBillingCountries } from './Checkout/PaymentForm/useBillingCountries';
import { PaywallLocation } from './enums/PaywallLocation';
import { ResumeSubscriptionModal, useResumeSubscription } from './ResumeSubscription';
import { useOpenCallRedirect } from './useOpenCallRedirect';
import { useSessionBasedCouponRedemption } from './useSessionBasedCoupon';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '../Heap';
import { PromotionModal } from './PromotionModal/PromotionModal';
import { usePaywallModalTypes } from './usePaywallModalTypes';

export enum PurchaseResultType {
  Success = 'SUCCESS',
  Canceled = 'CANCEL',
  Error = 'ERROR',
}

export type ResolvedPurchase = {
  type: PurchaseResultType;
  completedSubscriptionPurchase?: ReturnType<typeof usePayment>['completedPurchase'];
  completedPurchaseError?: ReturnType<typeof usePayment>['completePurchaseError'];
};

export type ShowBillingPopupParams = {
  location?: PaywallLocation;
  mediaIds?: number[];
};

export type BillingPopupContextType = {
  visible: boolean;
  location: PaywallLocation;
  show: (data: ShowBillingPopupParams) => { getPurchaseResult: () => Promise<ResolvedPurchase> };
  close: () => void;
  subscription: ReturnType<typeof useUserContext>['subscription'];
  previousSubscription: ReturnType<typeof useUserContext>['previousSubscription'];
  countryCode: ReturnType<typeof useUserContext>['myAccount']['country']['code'];
  plansError: ReturnType<typeof usePlans>['error'];
  plansLoading: ReturnType<typeof usePlans>['loading'];
  walletLoading: ReturnType<typeof useBillingHook>['loading'];
  walletError: ReturnType<typeof useBillingHook>['error'];
  setSelectedPlan: (plan: PlanType) => void;
  selectedPlan: PlanType;
  mediaIds?: number[];
  countryOptions: ReturnType<typeof useBillingCountries>['countryOptions'];
  getCountryName: ReturnType<typeof useBillingCountries>['getCountryName'];
  billingCountriesLoading: ReturnType<typeof useBillingCountries>['countriesLoading'];
  billingCountriesError: ReturnType<typeof useBillingCountries>['countriesError'];
} & ReturnType<typeof useBillingHook> &
  ReturnType<typeof usePayment> &
  ReturnType<typeof usePlans> &
  ReturnType<typeof usePaywallModalTypes>;

export interface UseMediaBankPaywallProps {
  location?: PaywallLocation;
}

const EMPTY_CTX_VALUES = (): BillingPopupContextType => ({
  addPaymentError: null,
  addPaymentLoading: false,
  applyCoupon: null,
  applyCouponError: null,
  close: null,
  completedPurchase: null,
  completePurchase: null,
  completePurchaseError: null,
  completePurchaseLoading: false,
  countryCode: null,
  countryOptions: [],
  getCountryName: () => undefined,
  billingCountriesLoading: false,
  billingCountriesError: null,
  couponLoading: false,
  currencyCode: null,
  currentPlan: null,
  currentPurchase: null,
  error: null,
  isAnnualRenewalDue: false,
  isReactivate: false,
  isTrialEligible: false,
  isWithinTrialPeriod: false,
  isEligibleForCardlessTrial: false,
  loading: false,
  location: null,
  onAddPaymentOption: null,
  onRemovePaymentOption: null,
  removePaymentError: null,
  removePaymentLoading: null,
  onUpdatePaymentOption: null,
  updatePaymentLoading: null,
  updatePaymentError: null,
  plans: null,
  plansError: null,
  plansLoading: false,
  handleRefetchPlans: null,
  discountType: null,
  removeCoupon: null,
  selectedPaymentOption: null,
  selectedPlan: null,
  setCompletedPurchase: null,
  setCompletePurchaseError: null,
  setSelectedPaymentOption: null,
  setSelectedPlan: null,
  setShowAddPaymentMethod: null,
  show: null,
  showAddPaymentMethod: false,
  subscription: null,
  previousSubscription: null,
  visible: false,
  wallet: null,
  walletLoading: false,
  walletError: null,
  mediaIds: null,
  setVisibleError: null,
  modalVariant: null,
  modalType: null,
});

const BillingPopupContext = createContext<BillingPopupContextType>(EMPTY_CTX_VALUES());

export const useBillingPopup = () => useContext(BillingPopupContext);

export type BillingPopupProviderState = {
  visible: boolean;
  /** pass location as a prop when rendering the paywall as a full-page (signup flow)
   * otherwise location should be passed as a param when calling show()
   */
  location?: PaywallLocation;
  // mediaIds are used to purchase media in paywall
  mediaIds?: number[];
};

const DEFAULT_POPUP_STATE: BillingPopupProviderState = {
  visible: false,
  location: null,
  mediaIds: null,
};

export const BillingPopupProvider: FC<{ location?: PaywallLocation }> = ({
  children,
  location: locationProp = null,
}) => {
  const [state, setState] = useState<BillingPopupProviderState>({ ...DEFAULT_POPUP_STATE });
  const resolvedLocation = useMemo(
    () => locationProp || state.location || PaywallLocation.GLOBAL_NAV,
    [locationProp, state.location]
  );

  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const { myAccount, subscription, artistId, previousSubscription } = useUserContext();
  const { isPausedSubscription, setOpen: setResumeModalOpen, isOpen: isResumeModalOpen } = useResumeSubscription();
  const countryCode = myAccount?.country?.code ?? 'US';
  const artistCount = myAccount?.artists?.length || 0;
  const isRegistration = (artistCount <= 1 && !subscription) || resolvedLocation === PaywallLocation.SIGNUP;
  const currentArtist = myAccount?.artists?.find((artist) => artist?.artistId === artistId);
  const hasNoPersonalProfile = !currentArtist?.profiles?.some((profile) => profile?.isPersonal);

  const { redirectToOpenCall } = useOpenCallRedirect();
  const { modalVariant, modalType } = usePaywallModalTypes();
  const {
    countryOptions,
    getCountryName,
    countriesLoading: billingCountriesLoading,
    countriesError: billingCountriesError,
  } = useBillingCountries();

  const {
    plans,
    error: plansError,
    loading: plansLoading,
    isAnnualRenewalDue,
    isTrialEligible,
    isWithinTrialPeriod,
    isReactivate,
    currentPlan,
    handleRefetchPlans,
    isEligibleForCardlessTrial,
    discountType,
  } = usePlans({ subscription, paywallLocation: resolvedLocation, artistId, isModal: state.visible, modalVariant });

  const {
    wallet,
    selectedPaymentOption,
    setSelectedPaymentOption,
    loading: walletLoading,
    error: walletError,
    onAddPaymentOption,
    showAddPaymentMethod,
    setShowAddPaymentMethod,
    addPaymentLoading,
    addPaymentError,
    onRemovePaymentOption,
    onUpdatePaymentOption,
    removePaymentError,
    removePaymentLoading,
    updatePaymentError,
    updatePaymentLoading,
    setVisibleError,
  } = useBillingHook({
    currentActiveSubscription: subscription,
  });

  const {
    currentPurchase,
    currencyCode,
    applyCoupon,
    removeCoupon,
    completePurchase,
    completedPurchase,
    applyCouponError,
    couponLoading,
    completePurchaseLoading,
    completePurchaseError,
    setCompletedPurchase,
    setCompletePurchaseError,
  } = usePayment({
    selectedPlan,
    selectedPaymentOption,
    paywallLocation: resolvedLocation,
    isAnnualRenewalDue,
    redirectToOpenCall,
    handleRefetchPlans,
    isModal: state.visible,
  });

  useSessionBasedCouponRedemption({
    applyCoupon,
    appliedCouponCode: currentPurchase?.coupon?.code,
  });

  // The deferred promise allows us to hold onto a promise until the paywall is closed
  // this allows the code that opens the paywall to await on the result of the paywall
  // and make a decision based on the result
  const { deferRef, defer } = useDeferredPromise<ResolvedPurchase>();

  // when the billing popup is opened, we return a function to the caller (getPurchaseResult)
  // if they invoke that function, we create a promise, store it in a ref and return it
  // the caller can then await on that promise to get the result of the billing popup without
  // needing a useEffect or other mechanism to listen for the result and try to infer what happened
  const showBillingPopup = useCallback(
    (data: ShowBillingPopupParams) => {
      // if the users's subscription is paused,
      // we don't want to show the paywall but instead show the unpause modal
      // so they can resume their paused subscription
      if (isPausedSubscription) {
        setResumeModalOpen(true);
        return {};
      }
      const { location, mediaIds } = data || {};
      setState({ visible: true, location, mediaIds });

      try {
        if (data?.location) {
          captureHeapEvent({
            name: HEAP_EVENT_NAMES.DISPLAY_UPGRADE_MODAL,
            data: {
              location,
              discountType: plans?.find((plan) => plan?.discountType)?.discountType,
              modalType,
            },
          });
        }
      } catch (e) {
        captureExceptionEvent(e, {
          section: 'Talent: heap "Display Upgrade Modal" event',
        });
      }

      return {
        getPurchaseResult: () => defer().promise,
      };
    },
    [isPausedSubscription, setResumeModalOpen, plans, defer, modalType]
  );

  // when the billing popup is closed, if the deferred promise exists, we resolve it with context of what happened
  // if the user closes the paywall without purchasing we resolve with a canceled status
  // if the user completes the purchase we resolve with a success status and the details of the purchase
  const closeBillingPopup = useCallback(() => {
    setState({ ...DEFAULT_POPUP_STATE });

    if (deferRef?.current) {
      let type = PurchaseResultType.Canceled;

      if (completePurchaseError) {
        type = PurchaseResultType.Error;
      }

      if (completedPurchase) {
        type = PurchaseResultType.Success;
      }

      const purchase: ResolvedPurchase = {
        completedPurchaseError: completePurchaseError,
        completedSubscriptionPurchase: completedPurchase,
        type,
      };
      deferRef.current?.resolve(purchase);
      deferRef.current = null;
    }
  }, [deferRef, completePurchaseError, completedPurchase]);

  const billingContext = useMemo(() => {
    return {
      // billing pop up values
      visible: state.visible,
      mediaIds: state.mediaIds,
      location: resolvedLocation,
      show: showBillingPopup,
      close: closeBillingPopup,
      subscription,
      previousSubscription,
      countryCode,
      // useBillingCountries
      countryOptions,
      getCountryName,
      billingCountriesLoading,
      billingCountriesError,
      // usePlans
      plans,
      isTrialEligible,
      isWithinTrialPeriod,
      isEligibleForCardlessTrial,
      isReactivate,
      plansLoading,
      plansError,
      discountType,
      // usePaywallModalTypes
      modalVariant,
      modalType,
      // useBilling
      wallet,
      walletLoading,
      walletError,
      selectedPaymentOption,
      setSelectedPaymentOption,
      onAddPaymentOption,
      showAddPaymentMethod,
      addPaymentLoading,
      setShowAddPaymentMethod,
      addPaymentError,
      onRemovePaymentOption,
      onUpdatePaymentOption,
      removePaymentError,
      removePaymentLoading,
      updatePaymentError,
      updatePaymentLoading,
      setVisibleError,
      // usePayment
      currentPurchase,
      currencyCode,
      applyCoupon,
      removeCoupon,
      completePurchase,
      completedPurchase,
      applyCouponError,
      couponLoading,
      completePurchaseLoading,
      completePurchaseError,
      setCompletedPurchase,
      setCompletePurchaseError,
      // aggregate attributes:
      setSelectedPlan,
      selectedPlan,
      // additional properties
      error: plansError,
      loading: plansLoading,
      isAnnualRenewalDue,
      currentPlan,
    };
  }, [
    addPaymentError,
    addPaymentLoading,
    applyCoupon,
    applyCouponError,
    closeBillingPopup,
    completedPurchase,
    completePurchase,
    completePurchaseError,
    completePurchaseLoading,
    countryCode,
    countryOptions,
    getCountryName,
    billingCountriesLoading,
    billingCountriesError,
    couponLoading,
    currencyCode,
    currentPlan,
    currentPurchase,
    isAnnualRenewalDue,
    isReactivate,
    isTrialEligible,
    isWithinTrialPeriod,
    isEligibleForCardlessTrial,
    onAddPaymentOption,
    plans,
    plansError,
    plansLoading,
    modalVariant,
    modalType,
    discountType,
    removeCoupon,
    resolvedLocation,
    selectedPaymentOption,
    selectedPlan,
    setCompletedPurchase,
    setCompletePurchaseError,
    setSelectedPaymentOption,
    setShowAddPaymentMethod,
    showAddPaymentMethod,
    showBillingPopup,
    state.visible,
    state.mediaIds,
    subscription,
    previousSubscription,
    wallet,
    walletLoading,
    walletError,
    onRemovePaymentOption,
    onUpdatePaymentOption,
    setVisibleError,
    removePaymentError,
    removePaymentLoading,
    updatePaymentError,
    updatePaymentLoading,
  ]);

  return (
    <BillingPopupContext.Provider value={billingContext}>
      <ResumeSubscriptionModal isOpen={isResumeModalOpen} onClose={() => setResumeModalOpen(false)} />
      {children}
      {!isRegistration && !hasNoPersonalProfile && (
        <PromotionModal
          setSelectedPlan={setSelectedPlan}
          showBillingPopup={showBillingPopup}
          subscription={subscription}
          previousSubscription={previousSubscription}
          artistId={artistId}
          plans={plans}
        />
      )}
    </BillingPopupContext.Provider>
  );
};

export const useMediaBankPaywall = (
  { location }: UseMediaBankPaywallProps = { location: PaywallLocation.MEDIA_BANK }
) => {
  const billing = useBillingPopup();

  return useCallback(
    ({ location: cbLocation }: UseMediaBankPaywallProps = {}) =>
      billing.show({
        location: cbLocation || location,
      }),
    [billing, location]
  );
};
